import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Boxes, FlaskConical, Compass } from "lucide-react";
import SystemsDiagramHero from "../components/SystemsDiagramHero";
import NewsletterForm from "../components/NewsletterForm";
import CarpetCardDownload from "../components/CarpetCardDownload";
import WaitlistModal from "../components/WaitlistModal";
import TypeBadge from "../components/TypeBadge";
import Seo from "../components/Seo";
import { lensBySlug } from "../data/content";
import { PRODUCTS } from "../data/products";
import { api } from "../lib/api";

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};
const Reveal = ({ children, i = 0, className = "" }) => (
  <motion.div variants={fade} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className={className}>
    {children}
  </motion.div>
);

function ThreeWays() {
  const cardBase = "group relative flex flex-col h-full bg-surface border border-border p-8 transition-colors hover:bg-surface-elevated overflow-hidden";
  const ctaCls = "mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-foreground/60 group-hover:text-primary group-hover:gap-3 transition-all";
  return (
    <section data-testid="three-ways" className="border-b border-border">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <Reveal className="max-w-2xl mb-12">
          <p className="mono-label">Where to begin</p>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">Three Ways to Get Started</h2>
          <p className="mt-4 text-base text-foreground/60 leading-relaxed">
            Choose the path that best fits where you are today. Learn the science, experience lower-tox
            alternatives, or begin implementing lasting changes.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          {/* LEARN */}
          <Reveal i={0}>
            <Link to="/greenpapers" data-testid="three-ways-learn" className={cardBase}>
              <span className="absolute top-0 left-0 h-[3px] w-full opacity-60 group-hover:opacity-100 transition-opacity" style={{ background: "#00E599" }} />
              <div className="flex items-center justify-between">
                <span className="flex h-11 w-11 items-center justify-center border" style={{ borderColor: "#00E59966", color: "#00E599" }}><BookOpen className="h-5 w-5" strokeWidth={1.5} /></span>
                <span className="mono-label" style={{ color: "#00E599" }}>Learn</span>
              </div>
              <h3 className="mt-6 font-display text-2xl font-medium">Greenpapers</h3>
              <p className="mt-3 text-sm text-foreground/60 leading-relaxed flex-1">
                Research-backed analyses that help you understand what matters before making changes.
              </p>
              <span className={ctaCls}>Explore Greenpapers <ArrowRight className="h-3.5 w-3.5" /></span>
            </Link>
          </Reveal>

          {/* TRY */}
          <Reveal i={1}>
            <div data-testid="three-ways-try" className={`${cardBase} hover:bg-surface`}>
              <span className="absolute top-0 left-0 h-[3px] w-full opacity-60" style={{ background: "#43C6F0" }} />
              <div className="flex items-center justify-between">
                <span className="flex h-11 w-11 items-center justify-center border" style={{ borderColor: "#43C6F066", color: "#43C6F0" }}><FlaskConical className="h-5 w-5" strokeWidth={1.5} /></span>
                <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border rounded-full" style={{ color: "#43C6F0", borderColor: "#43C6F066", backgroundColor: "#43C6F012" }}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#43C6F0" }} />Coming Soon
                </span>
              </div>
              <p className="mt-6 mono-label" style={{ color: "#43C6F0" }}>Try</p>
              <h3 className="mt-2 font-display text-2xl font-medium">Sample Library</h3>
              <p className="mt-3 text-sm text-foreground/60 leading-relaxed flex-1">
                Experience carefully selected lower-tox products before making the switch. Reduce decision risk by
                trying products before committing to full-size purchases.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
                <WaitlistModal
                  slug="sample-library"
                  name="Sample Library"
                  triggerTestid="three-ways-try-waitlist"
                  triggerClassName="inline-flex items-center gap-2 border border-primary text-primary px-5 py-3 font-mono text-[11px] uppercase tracking-widest cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  Join the Waitlist <ArrowRight className="h-3.5 w-3.5" />
                </WaitlistModal>
                <Link to="/sample-library" data-testid="three-ways-try-learnmore" className="font-mono text-[11px] uppercase tracking-widest text-foreground/50 hover:text-primary transition-colors">Learn more</Link>
              </div>
            </div>
          </Reveal>

          {/* IMPLEMENT */}
          <Reveal i={2}>
            <Link to="/products" data-testid="three-ways-implement" className={cardBase}>
              <span className="absolute top-0 left-0 h-[3px] w-full opacity-60 group-hover:opacity-100 transition-opacity" style={{ background: "#FFB259" }} />
              <div className="flex items-center justify-between">
                <span className="flex h-11 w-11 items-center justify-center border" style={{ borderColor: "#FFB25966", color: "#FFB259" }}><Boxes className="h-5 w-5" strokeWidth={1.5} /></span>
                <span className="mono-label" style={{ color: "#FFB259" }}>Implement</span>
              </div>
              <h3 className="mt-6 font-display text-2xl font-medium">Products</h3>
              <p className="mt-3 text-sm text-foreground/60 leading-relaxed flex-1">
                Digital Blueprints, Frameworks, Toolkits, and carefully curated products that help you turn
                knowledge into practical action.
              </p>
              <span className={ctaCls}>Explore Products <ArrowRight className="h-3.5 w-3.5" /></span>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FeaturedGreenpapers() {
  const [papers, setPapers] = useState([]);
  useEffect(() => {
    api.get("/greenpapers").then((r) => {
      const all = r.data;
      const featured = all.filter((p) => p.start_here);
      setPapers((featured.length ? featured : all).slice(0, 3));
    }).catch(() => setPapers([]));
  }, []);
  if (!papers.length) return null;
  return (
    <section className="border-b border-border bg-surface/30">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <Reveal className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <p className="mono-label">Learn · Featured Greenpapers</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">Start with the analyses<br className="hidden sm:block" /> people read first.</h2>
          </div>
          <Link to="/greenpapers" data-testid="featured-viewall" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary hover:gap-3 transition-all">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          {papers.map((p, i) => {
            const c = lensBySlug(p.category)?.color || "#00E599";
            return (
              <Reveal key={p.id} i={i}>
                <Link to={`/greenpapers/${p.slug}`} data-testid={`featured-card-${p.slug}`}
                  className="group relative flex flex-col h-full bg-surface hover:bg-surface-elevated p-7 transition-colors overflow-hidden">
                  <span className="absolute top-0 left-0 h-[3px] w-full opacity-60 group-hover:opacity-100 transition-opacity" style={{ background: c }} />
                  <span className="px-2 py-0.5 self-start font-mono text-[10px] uppercase tracking-widest border" style={{ color: c, borderColor: `${c}66` }}>{lensBySlug(p.category)?.title || p.category}</span>
                  <h3 className="mt-4 font-display text-xl font-medium leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm text-foreground/60 leading-relaxed flex-1 line-clamp-4">{p.summary}</p>
                  <span className="mt-6 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-foreground/50">Read <ArrowRight className="h-3.5 w-3.5" /></span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FeaturedProducts() {
  const featured = PRODUCTS.slice(0, 3);
  return (
    <section className="border-b border-border">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <Reveal className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <p className="mono-label">Implement · Featured Products</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">Turn knowledge<br className="hidden sm:block" /> into a plan.</h2>
          </div>
          <Link to="/products" data-testid="products-viewall" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary hover:gap-3 transition-all">
            View all products <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          {featured.map((p, i) => {
            const available = p.status === "available";
            return (
              <Reveal key={p.slug} i={i}>
                <Link to={`/products/${p.slug}`} data-testid={`home-product-${p.slug}`}
                  className="group relative flex flex-col h-full bg-surface hover:bg-surface-elevated p-7 transition-colors">
                  <div className="flex items-center justify-between gap-3">
                    <TypeBadge type={p.type} />
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border ${available ? "border-primary/50 text-primary bg-primary/5" : "border-border text-muted-foreground"}`}>
                      {available ? "Available" : "Coming soon"}
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-xl font-medium leading-snug group-hover:text-primary transition-colors">{p.name}</h3>
                  <p className="mt-3 text-sm text-foreground/60 leading-relaxed flex-1">{p.positioning}</p>
                  <div className="mt-6 flex items-center justify-between">
                    <span className="font-display text-lg font-medium text-foreground">{p.price || ""}</span>
                    <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-foreground/50 group-hover:text-primary transition-colors">{available ? "View" : "Notify me"} <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ServicesBand() {
  return (
    <section className="border-b border-border bg-[#0a241a] text-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 grid lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-7">
          <p className="mono-label text-primary">Optimize · Services</p>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">Want a faster, tailored path?<br className="hidden sm:block" /> Let's build it together.</h2>
          <p className="mt-5 max-w-xl text-base text-white/70 leading-relaxed">
            Work directly with My Eco Engineer, coaching, done-with-you household systems, and organizational
            design, applying the same engineering rigor behind our Greenpapers.
          </p>
        </Reveal>
        <Reveal i={1} className="lg:col-span-5 lg:flex lg:justify-end">
          <Link to="/services" data-testid="home-services-cta"
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium px-6 py-3.5 hover:bg-primary-hover transition-colors shadow-[0_0_20px_rgba(0,229,153,0.3)]">
            Explore Services <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div data-testid="home-page" className="home-white">
      <Seo
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "My Eco Engineer",
            url: "https://myecoengineer.com/",
            logo: "https://myecoengineer.com/brand/mee-favicon-512.png",
            description:
              "My Eco Engineer brings engineering principles to environmental health through research-backed Greenpapers, practical frameworks, Digital Blueprints, and carefully curated products.",
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "My Eco Engineer",
            url: "https://myecoengineer.com/",
          },
        ]}
      />
      {/* HERO */}
      <section className="relative grid-bg grain overflow-hidden border-b border-border">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-20">
          <div className="max-w-5xl">
            <p className="fade-up d1 mono-label">Engineering Better Living Systems</p>
            <h1 className="fade-up d2 mt-6 font-display font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              Reduce Toxic Exposure with{" "}
              <span className="text-primary">Systems, Not Guesswork.</span>
            </h1>
          </div>

          <div className="mt-8 grid lg:grid-cols-12 gap-x-14 gap-y-8 items-start">
            <div className="lg:col-span-7">
              <p className="fade-up d3 text-base md:text-lg text-foreground/70 leading-relaxed max-w-2xl">
                My Eco Engineer brings engineering principles to environmental health through research-backed
                Greenpapers, practical frameworks, Digital Blueprints, and carefully curated products. Everything we
                create is designed to help you make better environmental health decisions with clarity, confidence,
                and a systems-based approach.
              </p>
              <div className="fade-up d4 mt-8 flex flex-wrap gap-3">
                {[
                  { label: "Greenpapers", to: "/greenpapers", color: "#00E599", icon: BookOpen, testid: "hero-btn-greenpapers" },
                  { label: "Sample Library", to: "/sample-library", color: "#43C6F0", icon: FlaskConical, testid: "hero-btn-sample-library" },
                  { label: "Products", to: "/products", color: "#FFB259", icon: Boxes, testid: "hero-btn-products" },
                ].map((b) => (
                  <Link
                    key={b.to}
                    to={b.to}
                    data-testid={b.testid}
                    style={{ "--btn": b.color }}
                    className="group inline-flex items-center gap-2 border border-[color:var(--btn)] text-[color:var(--btn)] text-sm font-medium px-5 py-3.5 transition-colors hover:bg-[var(--btn)] hover:text-[#08120d]"
                  >
                    <b.icon className="h-4 w-4" strokeWidth={1.75} />
                    {b.label}
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                ))}
              </div>
            </div>
            <div className="fade-up d4 lg:col-span-5 lg:border-l-2 lg:border-primary/60 lg:pl-9">
              <p data-testid="hero-philosophy" className="font-display text-3xl md:text-4xl font-normal leading-[1.15] tracking-tight text-foreground/90">
                You don't need to replace everything.<br />You need to know <span className="text-primary font-medium">what matters most</span>.
              </p>
            </div>
          </div>

          <div className="fade-up d3 mt-14 relative border border-border bg-surface/40 p-5 sm:p-8 max-w-4xl mx-auto">
            <div className="absolute -top-2.5 left-5 bg-base px-2 mono-label">fig.01, exposure_system</div>
            <SystemsDiagramHero />
          </div>
        </div>
      </section>

      {/* brand statement transition */}
      <section data-testid="brand-statement" className="border-b border-border bg-surface/20">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-12 text-center">
          <p className="font-display text-xl md:text-2xl font-light leading-snug tracking-tight text-foreground/85">
            Research when you want to understand.{" "}
            <span className="text-primary">Products when you're ready to act.</span>
          </p>
        </div>
      </section>

      {/* THREE WAYS TO GET STARTED */}
      <ThreeWays />

      {/* FEATURED GREENPAPERS */}
      <FeaturedGreenpapers />

      {/* FEATURED PRODUCTS */}
      <FeaturedProducts />

      {/* SERVICES */}
      <ServicesBand />

      {/* NEWSLETTER */}
      <section id="subscribe" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <p className="mono-label">Free Resource</p>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">Get the Free Low-Tox<br />Quick-Start Checklist.</h2>
          <p className="mt-5 text-base text-foreground/65 leading-relaxed max-w-md">
            Identify the highest-impact changes before spending money on products or renovations. Plus receive
            every new Greenpaper as it's published, structured analysis, no noise, no trends.
          </p>
          <div data-testid="home-carpet-card" className="mt-8 max-w-md border border-border bg-surface p-5">
            <p className="mono-label text-primary">Also free when you join</p>
            <h3 className="mt-2 font-display text-lg font-medium leading-snug">Carpet: Keep vs. Replace card</h3>
            <p className="mt-2 text-sm text-foreground/60 leading-relaxed">
              A one-page, print-and-tick worksheet for deciding whether to keep, clean, or replace carpet, by condition, not by material type.
            </p>
            <div className="mt-4">
              <CarpetCardDownload
                testid="home-carpet-card-download"
                label="Get the Carpet Card"
                className="inline-flex items-center gap-2 border border-primary text-primary px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors"
              />
            </div>
          </div>
        </Reveal>
        <Reveal i={1} className="lg:pt-12">
          <NewsletterForm />
        </Reveal>
      </section>
    </div>
  );
}
